import * as React from 'react';
import { Button } from '@ministry/ui/internal';

import { translate } from '../i18n/strings.js';

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Top-level React error boundary — a screen that throws (a render bug, a bad response
 * shape a `try/catch` in a data-fetching hook didn't anticipate) must never show a blank
 * white page or a raw stack trace to staff. `DESIGN_SYSTEM.md`/SCREEN_SPECS I24's
 * "System Error" screen is the model this borrows its copy register from, kept minimal
 * here since the full I24 screen (with support contact details, etc.) is out of this
 * round's scope.
 *
 * Locale: this boundary can trigger before or during a render that itself threw, so it
 * cannot safely assume `LocaleProvider` context is still reachable — it reads
 * `document.documentElement.lang` directly instead of `useLocale()`.
 */
export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Structured, no PII, no secret — matches this app's fetch layer's own logging
    // discipline. A real deployment would forward this to the platform's own log
    // pipeline; there is no client-side error-reporting endpoint in this contract yet.
    // eslint-disable-next-line no-console -- the one sanctioned console use: last-resort client error visibility
    console.error('Unhandled render error', error, info.componentStack);
  }

  override render(): React.ReactNode {
    if (!this.state.error) return this.props.children;

    const locale = document.documentElement.lang === 'ne' ? 'ne' : 'en';

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-surface-page p-6 text-center">
        <h1 className="text-xl font-semibold text-text-heading">{translate(locale, 'errorBoundary.title')}</h1>
        <p className="max-w-sm text-sm text-text-muted">{translate(locale, 'errorBoundary.body')}</p>
        <Button onClick={() => window.location.reload()}>{translate(locale, 'errorBoundary.reload')}</Button>
      </div>
    );
  }
}
